import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy } from 'lucide-react';
import { Icone } from '../components/Icone';
import { Chargement, MessageErreur } from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import { BandeauEtatIa } from '../components/usage-ia/BandeauEtatIa';
import { ErreurApi } from '../lib/api';
import { useEtatIa, useGenerationIa, type ReponseFonctionIa } from '../lib/useUsageIa';

const CLASSE_CHAMP =
  'w-full min-h-[44px] rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-codialis-navy focus:outline-none focus:ring-2 focus:ring-codialis-mint';

/**
 * Génération IA de contenu (écran `instructor-ai-gen`). La demande passe par
 * la passerelle IA de l'API (TF-439), qui la compte au formateur et à la
 * formation choisie, et la refuse quand la direction a coupé l'IA, ne l'a pas
 * ouverte au compte (fermée de base, TF-474) ou que son quota du jour est
 * atteint. Seuls partent le sujet, le type de contenu et
 * l'intitulé de la formation.
 *
 * Sans prestataire raccordé, l'écran le dit, sans simuler de contenu.
 */
export function InstructorAiGenPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: etat, isLoading, error } = useEtatIa();
  const generation = useGenerationIa();
  const [formationId, setFormationId] = useState('');
  const [type, setType] = useState('');
  const [sujet, setSujet] = useState('');
  const [resultat, setResultat] = useState<ReponseFonctionIa | null>(null);

  useEffect(() => {
    if (!etat) return;
    if (!formationId) setFormationId(etat.formations.find((formation) => !formation.desactivee)?.id ?? '');
    if (!type) setType(etat.typesContenu[0]?.code ?? '');
  }, [etat, formationId, type]);

  const raccordee = etat?.fonctions.find((ligne) => ligne.fonction === 'GENERATION_CONTENU')?.raccordee ?? false;
  const formation = etat?.formations.find((ligne) => ligne.id === formationId);
  // La direction n'a pas de quota (`compte` vide) : rien ne la retient que le prestataire et la formation.
  const fermeAuCompte = (etat?.compte?.etat ?? 'AUTORISE') !== 'AUTORISE';
  const bloque = !raccordee || fermeAuCompte || Boolean(formation?.desactivee);

  const generer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    generation.mutate({ formationId, type, sujet: sujet.trim() }, { onSuccess: (reponse) => setResultat(reponse) });
  };

  const copier = (): void => {
    if (!resultat) return;
    navigator.clipboard
      .writeText(resultat.texte)
      .then(() => toast('Contenu copié'))
      .catch(() => toast('La copie n’a pas abouti : sélectionnez le texte à la main.'));
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-codialis-navy">Génération IA de contenu</h1>
          <p className="mt-1 text-sm text-codialis-gray">
            Une aide à la conception de vos quiz, ateliers et fiches de synthèse : chaque proposition se relit avant
            usage
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/instructor-courses')}
          className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-codialis-navy hover:bg-gray-50"
        >
          Retour à mes cours
        </button>
      </div>

      {isLoading && <Chargement libelle="Chargement…" />}
      {error && (
        <MessageErreur message={error instanceof ErreurApi ? error.message : 'L’état de l’IA n’a pas pu être lu.'} />
      )}

      {etat && <BandeauEtatIa etat={etat} fonction="GENERATION_CONTENU" />}

      {etat && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <form
            onSubmit={generer}
            className="min-w-0 space-y-4 rounded-2xl bg-white p-4 shadow-card sm:p-6 lg:col-span-1"
          >
            <h2 className="font-display text-base font-bold text-codialis-navy">Paramètres de génération</h2>
            <div>
              <label
                htmlFor="generation-formation"
                className="mb-1 block text-xs font-semibold uppercase text-codialis-navy"
              >
                Formation
              </label>
              <select
                id="generation-formation"
                value={formationId}
                onChange={(evenement) => setFormationId(evenement.target.value)}
                required
                className={CLASSE_CHAMP}
              >
                {etat.formations.length === 0 && <option value="">Aucune formation sur vos antennes</option>}
                {etat.formations.map((ligne) => (
                  <option key={ligne.id} value={ligne.id}>
                    {ligne.titre}
                    {ligne.desactivee ? ' · IA désactivée' : ''}
                  </option>
                ))}
              </select>
              {formation?.desactivee && (
                <p className="mt-1 text-xs text-red-600">
                  L’IA est désactivée pour cette formation par la direction. Motif : {formation.desactivee.motif}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="generation-type"
                className="mb-1 block text-xs font-semibold uppercase text-codialis-navy"
              >
                Type de contenu
              </label>
              <select
                id="generation-type"
                value={type}
                onChange={(evenement) => setType(evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                {etat.typesContenu.map((ligne) => (
                  <option key={ligne.code} value={ligne.code}>
                    {ligne.libelle}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="generation-sujet"
                className="mb-1 block text-xs font-semibold uppercase text-codialis-navy"
              >
                Sujet ou compétence visée
              </label>
              <input
                id="generation-sujet"
                type="text"
                required
                minLength={3}
                maxLength={300}
                value={sujet}
                onChange={(evenement) => setSujet(evenement.target.value)}
                placeholder="Par exemple : accueil du client au comptoir"
                aria-describedby="generation-sujet-aide"
                className={CLASSE_CHAMP}
              />
              <p id="generation-sujet-aide" className="mt-1 text-[11px] text-codialis-gray">
                Ne citez ni apprenant ni entreprise : le sujet part chez le prestataire.
              </p>
            </div>
            {generation.error && (
              <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
                {generation.error instanceof ErreurApi ? generation.error.message : 'La génération a échoué.'}
              </p>
            )}
            <button
              type="submit"
              disabled={bloque || generation.isPending || !formationId || sujet.trim().length < 3}
              className="gradient-mint flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold text-codialis-navy transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icone nom="ia" className="h-4 w-4" />
              {generation.isPending ? 'Génération en cours…' : 'Générer une proposition'}
            </button>
          </form>

          <section className="min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-6 lg:col-span-2">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
              <div className="min-w-0">
                <h2 className="font-display text-lg font-bold text-codialis-navy">Proposition</h2>
                <span className="text-xs text-codialis-gray">
                  {resultat ? `Proposée par ${resultat.prestataire} · à relire avant tout usage` : 'Rien de généré'}
                </span>
              </div>
              {resultat && (
                <button
                  type="button"
                  onClick={copier}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep"
                >
                  <Copy className="h-4 w-4" aria-hidden />
                  Copier
                </button>
              )}
            </div>
            {resultat ? (
              <div className="whitespace-pre-wrap break-words rounded-xl border border-gray-100 bg-gray-50/70 p-4 text-sm text-codialis-navy">
                {resultat.texte}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-codialis-gray">
                  <Icone nom="ia" className="h-6 w-6" />
                </div>
                <p className="max-w-sm text-sm text-codialis-gray">
                  {raccordee
                    ? 'La proposition s’affichera ici. Elle n’est enregistrée nulle part : copiez ce que vous gardez.'
                    : 'Aucun prestataire d’IA n’est raccordé : la génération sera disponible une fois le prestataire choisi.'}
                </p>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
